import { Search } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { formatNumber } from "../../utils/format.ts";
import { isJobActive } from "../../utils/status.ts";
import AgentRunRow from "../agent/AgentRunRow.tsx";
import { FoldList, FoldRow, useOpenFolds } from "../FoldList.tsx";
import FormattedDate from "../FormattedDate.tsx";
import RelativeTime from "../RelativeTime.tsx";
import SignalBriefPanel from "./SignalBriefPanel.tsx";
import { useSignalResearch } from "./useSignalResearch.ts";

/**
 * A signal's research: start or re-run it, the agent's latest brief, and folded
 * beneath it every run (with its transcript, for developers) and the briefs
 * before it. The signal
 * log's drawer and a signal hit in the prospect drawer show the same.
 */
export default function SignalResearchPanel({
  signalId,
  canResearch,
}: {
  signalId: string;
  // Starting research spends the org's budget: the scope that edits workflows.
  canResearch: boolean;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useSignalResearch(signalId);
  const folds = useOpenFolds(signalId);
  const start = useMutation({
    mutationFn: () => api.startSignalResearch(signalId),
    onSuccess: () => {
      toast.success("Research agent started");
      queryClient.invalidateQueries({ queryKey: keys.signal.research(signalId) });
      queryClient.invalidateQueries({ queryKey: keys.signal.all });
    },
  });

  if (isLoading) return <CircularProgress size={20} />;
  if (!data) return null;
  const { runs, briefs } = data;
  const latestBrief = briefs[0];
  const running = runs.some((r) => isJobActive(r.status));

  return (
    <Stack spacing={2}>
      {canResearch && (
        <Box>
          <Tooltip title={data.unavailable_reason ?? ""}>
            <span>
              <Button
                variant={runs.length ? "outlined" : "contained"}
                size="small"
                startIcon={<Search />}
                disabled={!data.can_research || running || start.isPending}
                onClick={() => start.mutate()}
              >
                {runs.length ? "Re-run research" : "Research"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {!data.can_research && runs.length === 0 && <Alert severity="info">{data.unavailable_reason}</Alert>}

      {running && !latestBrief && (
        <Alert severity="info" icon={<CircularProgress size={16} />}>
          The research agent is working. Its brief appears here when it is done.
        </Alert>
      )}

      {latestBrief ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <SignalBriefPanel brief={latestBrief} />
        </Paper>
      ) : (
        !running &&
        data.can_research && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Not researched: the signal matcher's match only.
          </Typography>
        )
      )}

      {(runs.length > 0 || briefs.length > 1) && (
        <FoldList>
          {runs.length > 0 && (
            <FoldRow
              label="Runs"
              summary={
                <>
                  {formatNumber(runs.length)} · last <RelativeTime value={runs[0].created_at} />
                </>
              }
              expanded={folds.isOpen("runs")}
              onToggle={() => folds.toggle("runs")}
            >
              <Stack spacing={1.5}>
                {runs.map((run) => (
                  <AgentRunRow
                    key={run.id}
                    run={run}
                    fetchTranscript={(afterSeq) => api.getSignalResearchTranscript(signalId, run.id, afterSeq)}
                    // Research files a brief and contact research adds people
                    // (both shown elsewhere); other agents report here.
                    showReport={run.agent_type !== "signal_research" && run.agent_type !== "contact_research"}
                  />
                ))}
              </Stack>
            </FoldRow>
          )}
          {briefs.length > 1 && (
            <FoldRow
              label="Earlier briefs"
              summary={formatNumber(briefs.length - 1)}
              expanded={folds.isOpen("briefs")}
              onToggle={() => folds.toggle("briefs")}
            >
              <Stack spacing={2}>
                {briefs.slice(1).map((b) => (
                  <Paper key={b.id} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      <FormattedDate value={b.created_at} format="datetime" />
                    </Typography>
                    <SignalBriefPanel brief={b} />
                  </Paper>
                ))}
              </Stack>
            </FoldRow>
          )}
        </FoldList>
      )}
    </Stack>
  );
}
